import { ExternalLink, Clock } from "lucide-react";
import { format, addDays } from "date-fns";
import { majorAirports, getAirportTimezoneAbbr } from "@/lib/airports";
import { calculateFlightTime, calculateLandingTime } from "@/lib/flightCalculations";

type FlightSearchLinksProps = {
  origin?: string;
  destination?: string;
  departureDate?: Date;
  returnDate?: Date;
  label?: string;
  /** Preferred takeoff time window start in minutes from midnight (e.g., 480 = 8:00 AM) */
  takeoffWindowStart?: number;
  /** Preferred takeoff time window end in minutes from midnight (e.g., 720 = 12:00 PM) */
  takeoffWindowEnd?: number;
  /** Preferred landing time window end in minutes from midnight */
  landingWindowEnd?: number;
  /** For return flight: preferred takeoff time window */
  returnTakeoffWindowStart?: number;
  returnTakeoffWindowEnd?: number;
};

/**
 * Convert minutes from midnight to "HH:MM AM/PM" format for display.
 * No day suffix - dates are shown separately.
 */
function minutesToTimeDisplay(minutes: number): string {
  // Normalize to 0-1439 range
  let displayMinutes = ((minutes % 1440) + 1440) % 1440;
  
  const h = Math.floor(displayMinutes / 60) % 24;
  const m = displayMinutes % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  
  return `${displayH}:${m.toString().padStart(2, '0')} ${period}`;
}

export function FlightSearchLinks({
  origin,
  destination,
  departureDate,
  returnDate,
  label,
  takeoffWindowStart,
  takeoffWindowEnd,
  landingWindowEnd,
  returnTakeoffWindowStart,
  returnTakeoffWindowEnd,
}: FlightSearchLinksProps) {
  if (!departureDate) return null;

  const dep = format(departureDate, "yyyy-MM-dd");
  const ret = returnDate ? format(returnDate, "yyyy-MM-dd") : null;

  // Calculate timezone-aware flight duration and landing day offset
  const flightTime = origin && destination ? calculateFlightTime(origin, destination) : null;
  
  // Calculate landing time with timezone offset
  let landingDayOffset = 0;
  let landingTimeMinutes = 0;
  if (flightTime && takeoffWindowStart !== undefined) {
    const landing = calculateLandingTime(takeoffWindowStart, flightTime);
    landingDayOffset = landing.dayOffset;
    landingTimeMinutes = landing.landingMinutes;
  }

  // Get timezone abbreviations for display
  const originTz = origin ? getAirportTimezoneAbbr(origin) : '';
  const destTz = destination ? getAirportTimezoneAbbr(destination) : '';

  // Format flight duration
  let flightDurations: { nonstop: string; oneStop: string } | null = null;
  if (flightTime) {
    const oneStopMinutes = flightTime.flightDurationMinutes + 150;
    flightDurations = {
      nonstop: flightTime.durationFormatted,
      oneStop: `${Math.floor(oneStopMinutes / 60)}h ${oneStopMinutes % 60}m`
    };
  }

  // Build time filter display
  const hasTimeFilters = takeoffWindowStart !== undefined || landingWindowEnd !== undefined;
  
  // === KAYAK ===
  const kayakPath = [
    origin && destination ? `${origin}-${destination}` : destination ? `-${destination}` : origin ? `${origin}-` : "",
    dep,
    ret,
  ]
    .filter(Boolean)
    .join("/");
  
  const kayakUrl = kayakPath ? `https://www.kayak.com/flights/${kayakPath}?sort=bestflight_a` : "";

  // === SKYSCANNER ===
  const skyDep = format(departureDate, "yyMMdd");
  const skyRet = returnDate ? format(returnDate, "yyMMdd") : null;
  const skyOrigin = (origin || "").toLowerCase();
  const skyDest = (destination || "").toLowerCase();
  const skyscannerPath = [
    skyOrigin || "anywhere",
    skyDest || "anywhere",
    skyDep,
    skyRet,
  ]
    .filter(Boolean)
    .join("/");
  
  // Build Skyscanner departure-times param
  let skyscannerParams: string[] = [];
  if (takeoffWindowStart !== undefined && takeoffWindowEnd !== undefined) {
    const outboundTimes = `${takeoffWindowStart}-${takeoffWindowEnd}`;
    if (returnDate && returnTakeoffWindowStart !== undefined && returnTakeoffWindowEnd !== undefined) {
      const inboundTimes = `${returnTakeoffWindowStart}-${returnTakeoffWindowEnd}`;
      skyscannerParams.push(`departure-times=${outboundTimes},${inboundTimes}`);
    } else {
      skyscannerParams.push(`departure-times=${outboundTimes}`);
    }
  } else if (returnDate && returnTakeoffWindowStart !== undefined && returnTakeoffWindowEnd !== undefined) {
    skyscannerParams.push(`departure-times=${returnTakeoffWindowStart}-${returnTakeoffWindowEnd}`);
  }
  
  const skyscannerQueryString = skyscannerParams.length > 0 ? `?${skyscannerParams.join('&')}` : '';
  const skyscannerUrl = `https://www.skyscanner.com/transport/flights/${skyscannerPath}/${skyscannerQueryString}`;

  // === GOOGLE FLIGHTS ===
  // Build time filter hints for Google Flights
  // Google Flights uses arrival time for outbound and departure time for return
  let googleTimeHints = '';
  
  // Add arrival time hint for outbound (landing time)
  if (landingTimeMinutes !== undefined && landingTimeMinutes > 0) {
    const arrivalHour = Math.floor((landingTimeMinutes % 1440) / 60);
    const arrivalPeriod = arrivalHour < 12 ? 'morning' : arrivalHour < 17 ? 'afternoon' : 'evening';
    googleTimeHints += `%20arriving%20${arrivalPeriod}`;
  }
  
  // Add departure time hint for return
  if (returnTakeoffWindowStart !== undefined) {
    const returnHour = Math.floor((returnTakeoffWindowStart % 1440) / 60);
    const returnPeriod = returnHour < 6 ? 'early%20morning' : returnHour < 12 ? 'morning' : returnHour < 17 ? 'afternoon' : 'evening';
    googleTimeHints += `%20return%20departing%20${returnPeriod}`;
  }
  
  const googleFlightsUrl = `https://www.google.com/travel/flights?q=Flights%20from%20${origin || ''}%20to%20${destination || ''}%20on%20${dep}${ret ? `%20returning%20${ret}` : ''}${googleTimeHints}&curr=USD`;

  const displayLabel = label ?? (returnDate ? "Search round-trip" : "Search flights");

  // Calculate landing date for display
  const landingDate = landingDayOffset > 0 ? addDays(departureDate, landingDayOffset) : departureDate;

  // Calculate return date accounting for next-day departures
  const returnDepartDate = returnTakeoffWindowStart !== undefined && returnTakeoffWindowStart >= 1440 && returnDate
    ? addDays(returnDate, Math.floor(returnTakeoffWindowStart / 1440))
    : returnDate;

  return (
    <div className="mt-1 space-y-1">
      {hasTimeFilters && takeoffWindowStart !== undefined && (
        <div className="text-[9px] text-muted-foreground space-y-0.5">
          <div>
            ⏰ Depart {origin}: {format(departureDate, 'EEE MMM d')}, {minutesToTimeDisplay(takeoffWindowStart)} {originTz}
          </div>
          <div>
            → Land {destination}: {format(landingDate, 'EEE MMM d')}, ~{minutesToTimeDisplay(landingTimeMinutes)} {destTz}
          </div>
          {returnTakeoffWindowStart !== undefined && returnDepartDate && (
            <div className="mt-0.5">
              ⏰ Return: {format(returnDepartDate, 'EEE MMM d')}, {minutesToTimeDisplay(returnTakeoffWindowStart)} {destTz}
            </div>
          )}
        </div>
      )}
      
      <div className="flex flex-wrap items-center gap-3 text-[10px]">
        <span className="text-muted-foreground">{displayLabel}:</span>

        <a
          href={googleFlightsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Google
        </a>

        {kayakUrl && (
          <a
            href={kayakUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            Kayak
          </a>
        )}

        <a
          href={skyscannerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Skyscanner
        </a>
      </div>
    </div>
  );
}
